import 'isomorphic-fetch';
import { trimEnd, isString } from 'lodash';
import { createClient } from 'fetch-plus';
import defer from 'defer-promise';
import isAbsoluteUrl from 'is-absolute-url';

import * as middlewares from './middleware';
import defaultOptions from './defaultOptions';

const symbols = {
  auth: Symbol('Auth instance property'),
  configs: Symbol('Configs instance property'),
  utils: Symbol('Utils instance property'),
  options: Symbol('Options property'),
  client: Symbol('Client property'),
  deferredSelf: Symbol('Deferred self property'),
};

export default class Api {
  constructor(auth, configs, utils) {
    this[symbols.auth] = auth;
    this[symbols.configs] = configs;
    this[symbols.utils] = utils;
    this[symbols.options] = {};
    this[symbols.deferredSelf] = defer();
  }

  async init(options = {}) {
    this[symbols.options] = {
      ...defaultOptions,
      ...options,
    };

    const { baseUrl, strictBaseUrl } = this[symbols.options];

    if (!baseUrl) {
      throw new Error('`options.baseUrl` is required!');
    }

    const client = createClient(trimEnd(baseUrl, ' /'), {
      normalizeUrl: url => url && url.trim(),
    });

    client.addMiddleware(middlewares.normalizeUrl({
      baseUrl,
      strictBaseUrl,
    }));

    client.addMiddleware(middlewares.auth(this[symbols.auth], ::this.request));
    client.addMiddleware(middlewares.negotiate());
    client.addMiddleware(middlewares.formData());
    client.addMiddleware(middlewares.json());
    client.addMiddleware(middlewares.urlEncoded());
    client.addMiddleware(middlewares.text());

    this[symbols.client] = client;
    this[symbols.deferredSelf].resolve(this);

    return this;
  }

  addMiddleware(middleware) {
    this[symbols.deferredSelf].promise.then(() => {
      this[symbols.client].addMiddleware(middleware);
    });
  }

  /**
   * @param {string} path
   * @param {Object} options
   *
   * @returns Promise
   */
  request(path, options) {
    return this[symbols.client].request(path, options);
  }

  async uploadImage(file, options = {}) {
    const { images: configs } = this[symbols.options];
    const resize = this[symbols.utils].Images.resizeFile;

    const uploadType = isString(file) && isAbsoluteUrl(file) ? 'url' : 'file';
    const body = new FormData();
    const needResize = typeof options.needResize !== 'undefined' && options.needResize;

    if (uploadType === 'file' && needResize) {
      const resizeTo = this[symbols.configs].get('tulip.imageUpload', {});
      const resizedFile = await resize(file, {
        maxWidth: resizeTo.maxWidth,
        maxHeight: resizeTo.maxHeight,
      });

      body.append('file', resizedFile, resizedFile.name);
    } else {
      body.append('file', file);
    }

    body.append('uploadType', uploadType);
    body.append('options', JSON.stringify(options));

    return this.request(configs.uploadUrl, {
      method: 'POST',
      body,
    });
  }
}



// WEBPACK FOOTER //
// ./src/Api/Api.js